<template>
  <div
    class="relative min-w-full bg-cover h-full bg-center flex items-center justify-center"
    :style="{
      backgroundImage: `url(${require('../assets/images/grafokettbakgrundsbild2.png')})`,
    }"
  >
    <div class="loginbox mb-2 py-8">
      <login-box>
        <template v-slot:header>
          <div class="mb-4">
            <p class="text-2xl text-center">Welcome to Cloudlabel 🚀</p>
            <p class="text-sm mt-8 mb-0">
              {{
                oidc
                  ? "Finish your account registration"
                  : "Set up your account in order to get started"
              }}
            </p>
          </div>
        </template>
        <template>
          <div class="flex flex-col items-center">
            <div
              class="flex flex-col items-center justify-center mt-6 space-y-2 w-full"
            >
              <MicrosoftButton @click="() => loginWithProvider('microsoft')" />
              <GoogleButton @click="() => loginWithProvider('google')" />
            </div>
            <HorizontalLineText>
              <span class="text-xs">OR</span>
            </HorizontalLineText>
            <v-input
              class="pt-4"
              labelPlaceholder="E-mail"
              type="text"
              v-model="email"
              v-if="!!oidc"
              disabled
            />
            <v-input
              class="pt-4"
              labelPlaceholder="First name"
              type="text"
              v-model="firstName"
            />
            <v-input
              class="pt-4"
              labelPlaceholder="Last name"
              type="text"
              v-model="lastName"
            />
            <v-input
              :error="this.passwordStrengthError"
              @input="validatePasswordStrength"
              class="pt-4"
              :showPassword="true"
              labelPlaceholder="Enter password"
              type="password"
              v-model="enterPassword"
              v-if="!oidc"
            />
            <v-input
              class="pt-4"
              :showPassword="true"
              :error="this.passwordsMatchError"
              @input="validatePasswordMatch"
              labelPlaceholder="Confirm password"
              type="password"
              v-model="password"
              v-if="!oidc"
            />
            <div class="mt-2 flex mb-8 w-full">
              <Checkbox :value="policyAccepted" @input="policyChecked" />
              <span class="ml-3 flex-1">
                I have read and I understand the
                <a href="/#/policy" target="_blank" class="text-white">
                  Privacy Policy
                </a>
              </span>
            </div>
            <grafokett-button
              :class="{
                disable:
                  (!Boolean(oidc) &&
                    (password === '' || enterPassword === '')) ||
                  !policyAccepted,
              }"
              :disabled="
                (!Boolean(oidc) && (password === '' || enterPassword === '')) ||
                  !policyAccepted
              "
              class="primary p-6 focus:border-white w-full"
              @click="saveAccount"
              :error="this.message"
              >Save</grafokett-button
            >
          </div>
        </template>
      </login-box>
    </div>
    <Loading v-if="loading" :heading="'Loading'" />
    <Modal
      v-if="showModal"
      :type="modalType"
      :heading="modalHeader"
      :description="modalDescription"
      @close="$router.push({ name: 'login' })"
    />
  </div>
</template>

<script>
import vInput from "components/forms/Input.vue";
import GrafokettButton from "components/forms/Button.vue";
import LoginBox from "../components/LoginBox.vue";
import Loading from "../components/Loading";
import Checkbox from "../components/forms/Checkbox";
import Modal from "../components/Modal";
import MicrosoftButton from "../components/MicrosoftButton";
import GoogleButton from "../components/GoogleButton";
import { hashStringToObject } from "../utils/stringHelpers";
import { redirectUriProvider } from "../store/modules/auth";
import HorizontalLineText from "../components/HorizontalLineText";

export default {
  name: "invited-user",
  components: {
    Checkbox,
    vInput,
    GrafokettButton,
    LoginBox,
    Loading,
    Modal,
    MicrosoftButton,
    GoogleButton,
    HorizontalLineText,
  },
  data() {
    return {
      email: "",
      firstName: "",
      lastName: "",
      password: "",
      enterPassword: "",
      message: "",
      hash: "",
      oidc: "",
      policy: null,
      policyAccepted: false,
      loading: false,
      modalType: "error",
      modalHeader: "",
      modalDescription: "",
      showModal: false,
      passwordStrengthError: "",
      passwordsMatchError: "",
    };
  },
  methods: {
    showErrorMessage(error) {
      if (!error) return;
      if (error.status === 422) return error.data;
      return error.data.message;
    },
    async loginWithProvider(provider) {
      try {
        await this.$store.dispatch("AUTH/PROVIDER_LOGIN", {
          method: provider,
          type: "INVITE",
          state: this.hash,
        });
      } catch (error) {
        this.loading = false;
        this.modalType = "error";
        this.modalHeader = "Something went wrong";
        this.showModal = true;
      }
    },
    async saveAccount() {
      this.loading = true;
      const id = this.policy.id;
      const isOidc = Boolean(this.oidc);
      let payload = {
        firstName: this.firstName,
        lastName: this.lastName,
        invitationHash: this.hash,
        policyId: id,
      };

      if (isOidc) {
        payload = {
          ...payload,
          authenticatedWithOidc: true,
          email: this.email,
        };
      } else {
        payload = { ...payload, password: this.password };
      }

      try {
        await this.$store.$api.post("/api/account/invite/accept", payload);
        this.loading = false;
        this.modalType = "success";
        this.modalHeader = "Success";
        this.modalDescription =
          "Your account has been created. Closing this will redirect you to the login page";
        this.showModal = true;
      } catch {
        this.loading = false;
        this.modalType = "error";
        this.modalHeader = "Something went wrong";
        this.modalDescription =
          "Something went wrong when creating your account.";
        this.showModal = true;
      }
    },
    async getLatestPolicy() {
      const response = await this.$store.$api.get("/api/account/invite/policy");
      this.policy = response.data.data;
    },
    async preflight() {
      try {
        await this.$store.$api.post(
          `/api/account/invite/pre-check?hash=${this.hash}`
        );
      } catch {
        this.modalType = "error";
        this.modalHeader = "Invitation invalid";
        this.modalDescription =
          "This invitation is either expired or has been revoked. Contact your administrator for a new one";
        this.showModal = true;
      }
    },
    policyChecked() {
      this.policyAccepted = !this.policyAccepted;
    },
    validatePasswordStrength() {
      const passwordRegex = new RegExp(
        "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\\$%\\^&\\*])(?=.{8,})"
      );
      if (!passwordRegex.test(this.enterPassword)) {
        this.passwordStrengthError =
          "Password does not meet requirements. A password should be at least 8 characters long, contain both numbers, special characters, uppercase characters and lowercase characters";
      } else {
        this.passwordStrengthError = "";
      }
    },
    validatePasswordMatch() {
      if (this.password !== this.enterPassword) {
        this.passwordsMatchError = "Passwords does not match";
      } else {
        this.passwordsMatchError = "";
      }
    },
    async getProviderInfo() {
      try {
        const response = await this.$store.$auth.post(
          "/api/account/userInfoOidc",
          {
            code: this.oidc.code,
            state: decodeURIComponent(this.oidc.state),
            redirect_uri: redirectUriProvider,
          }
        );
        this.email = response.data.email;
        this.firstName = response.data.firstName;
        this.lastName = response.data.lastName;
      } catch (error) {
        this.$router.push({ name: "setupaccount", query: { hash: this.hash } });
      }
    },
  },
  computed: {
    errorMessage() {
      return this.$store.getters["AUTH/ERROR_MESSAGE"];
    },
  },
  watch: {
    "$route.query": {
      async handler(query) {
        this.hash = query.hash;
        this.oidc = query.oidc ? hashStringToObject(query.oidc) : null;
      },
      immediate: true,
    },
  },
  async mounted() {
    this.loading = true;
    this.preflight();
    this.getLatestPolicy();
    if (this.oidc) {
      await this.getProviderInfo();
    }
    this.loading = false;
  },
};
</script>

<style scoped lang="postcss">
.loginbox {
  box-shadow: 0px 0px 10px theme("colors.primary");
  max-width: 36rem;
  @apply w-full bg-primary rounded;
}
</style>
